export const DEFAULT_FACE_WIDTH = 50;
export const DEFAULT_SYSTEM_BRAND = "YKK AP";

export const TYPOLOGIES = [
  { value: "fixed_window", label: "Fixed window", type: "window", system: "YKK AP IWIN-S Fixed" },
  { value: "casement_window", label: "Casement window", type: "window", system: "YKK AP IWIN-S Casement" },
  { value: "top_hung_window", label: "Top hung window", type: "window", system: "YKK AP IWIN-S Top Hung" },
  { value: "tilt_turn_window", label: "Tilt & turn window", type: "window", system: "YKK AP IWIN-S Casement (Tilt & Turn)" },
  { value: "sliding_window", label: "Sliding window", type: "window", system: "YKK AP IWIN-S Sliding" },
  { value: "slim_sliding_window", label: "Slim sliding window", type: "window", system: "YKK AP IWIN-S Slim Sliding" },
  { value: "hinged_door_single", label: "Hinged door · single", type: "door", system: "YKK AP IWIN-S Casement Door" },
  { value: "hinged_door_double", label: "Hinged door · double", type: "door", system: "YKK AP IWIN-S Casement Door" },
  { value: "sliding_door", label: "Sliding door", type: "door", system: "YKK AP IWIN-S Sliding Door" },
  { value: "sliding_door_fixed", label: "Sliding door · single shutter + fixed", type: "door", system: "YKK AP IWIN-S Sliding Door" },
  { value: "storefront", label: "Shopfront / storefront", type: "door", system: "YKK AP YES 45 TU Storefront" },
  { value: "vertical_sliding_window", label: "Vertical sliding (hung sash)", type: "window", system: "YKK AP IWIN-S" },
  { value: "pivot_window", label: "Pivot window", type: "window", system: "YKK AP IWIN-S" },
  { value: "louvre_window", label: "Louvre window", type: "window", system: "YKK AP IWIN-E Louver" },
  { value: "folding_door", label: "Bi-fold door", type: "door", system: "YKK AP IWIN-S" },
  { value: "combination", label: "Combination (as drawn)", type: null, system: "YKK AP IWIN-S" },
];

export const PROFILE_PRESETS = [
  ...new Set(TYPOLOGIES.map((t) => t.system)),
  "YKK AP IWIN-E Sliding", "YKK AP IWIN-E Casement", "YKK AP IWIN-E Louver", "YKK AP YES 60 Storefront", "YKK AP 35D Entrance Door", "YKK AP 50D Entrance Door",
  "Schüco AWS 75.SI+", "Reynaers MasterLine 8", "Kawneer AA 720", "Alumil S67",
];

const mainRow = (item) => item.rows.reduce((best, h, i) => (h > item.rows[best] ? i : best), 0);

function splitForSlidingFixed(item) {
  const mr = mainRow(item), half = Math.floor(item.width / 2);
  return { ...item, columns: [half, item.width - half], spans: item.rows.length > 1 ? item.rows.map((_, r) => ({ r, c: 0, rs: 1, cs: 2 })).filter((sp) => sp.r !== mr) : [],
    cells: item.cells.map((row, r) => (r === mr ? ["fixed", "sliding_left"] : [row[0], row[0]])), panel_glass: {}, corners: {} };
}

export function convertTypology(item, value) {
  const t = TYPOLOGIES.find((x) => x.value === value);
  if (!t) return item;
  if (value === "sliding_door_fixed" && item.columns.length === 1) return { ...splitForSlidingFixed(item), typology: value, type: t.type, profile_system: t.system };
  const mr = mainRow(item), n = item.columns.length;
  const operable = item.cells[mr].some((v) => v !== "fixed");
  const left = (c) => c < n / 2;
  const map = {
    fixed_window: () => "fixed",
    casement_window: (c) => (left(c) ? "casement_left" : "casement_right"),
    top_hung_window: () => "awning",
    tilt_turn_window: (c) => (left(c) ? "tilt_turn_left" : "tilt_turn_right"),
    sliding_window: (c) => (left(c) ? "sliding_right" : "sliding_left"),
    slim_sliding_window: (c) => (left(c) ? "sliding_right" : "sliding_left"),
    sliding_door: (c) => (left(c) ? "sliding_right" : "sliding_left"),
    sliding_door_fixed: (c) => (c === n - 1 ? "sliding_left" : "fixed"),
    hinged_door_single: () => "door_left",
    hinged_door_double: (c) => (left(c) ? "door_left" : "door_right"),
    storefront: (c) => (n === 1 || c === Math.floor((n - 1) / 2) ? "door_left" : "fixed"),
    vertical_sliding_window: () => "sliding_up",
    pivot_window: () => "pivot_vertical",
    louvre_window: () => "louvre",
    folding_door: (c) => (left(c) ? "folding_left" : "folding_right"),
  }[value];
  const allInRow = ["sliding_window", "slim_sliding_window", "sliding_door", "sliding_door_fixed", "storefront", "fixed_window", "hinged_door_double", "louvre_window", "folding_door"].includes(value);
  const cells = !map ? item.cells : item.cells.map((row, r) => row.map((op, c) => {
    if (value === "fixed_window") return "fixed";
    if (r !== mr) return op;
    if (value === "hinged_door_single") return c === 0 ? "door_left" : "fixed";
    return allInRow || !operable || op !== "fixed" ? map(c) : op;
  }));
  return { ...item, typology: value, cells, type: t.type || item.type, profile_system: t.system };
}

export const GLASS_TYPES = ["Clear", "Clear toughened", "Tinted", "Reflective", "Low-E", "Frosted", "Laminated clear", "Laminated frosted"];
export const GLASS_PRESETS = [
  "6mm clear toughened", "6mm + 12A + 6mm clear DGU", "6mm Low-E + 12A + 6mm clear DGU", "6mm reflective + 12A + 6mm clear DGU",
  "8.38mm laminated clear", "6mm frosted toughened", "6.38mm laminated + 12A + 6mm clear DGU",
];

export function composeGlass({ construction, outer, outerType, gap, inner, innerType }) {
  if (construction === "single") return `${outer}mm ${outerType.toLowerCase()}`;
  return `${outer}mm ${outerType.toLowerCase()} + ${gap}A + ${inner}mm ${innerType.toLowerCase()} DGU`;
}
